import { useState, useEffect, useCallback, useRef } from "react";
import { StripeProvider } from "../../../Stripe/StripeProvider.tsx";
import { useCreateStripeOrder } from "../../CreateOrderProviders/useCreateStripeOrder.ts";
import { FullPageLoading } from "../../../UI/FullPageLoading.tsx";
import { PaymentStepComponentProps } from "../PaymentMethodStepInterface.ts";
import { CardInstallmentsPaymentForm } from "./CardInstallmentsPaymentComponents.tsx";

export function CardInstallmentsPaymentStep({
  checkoutData,
  item,
  itemType,
  analyticsParams,
  onSuccess,
  onError,
  onBack,
}: PaymentStepComponentProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const { createStripeOrder, loading } = useCreateStripeOrder();
  const [orderInfo, setOrderInfo] = useState<{
    normasTeachingItemName: string;
    amountCurrency: { amount: number; currency: string };
    returnUrl?: string;
  } | null>(null);

  const handleError = useCallback(
    (message: string) => {
      onError(message);
    },
    [onError],
  );

  const orderCreated = useRef(false);

  useEffect(() => {
    if (orderCreated.current) {
      console.log("Order already created, skipping");
      return;
    }

    const initStripe = async () => {
      orderCreated.current = true;

      try {
        const { order } = await createStripeOrder(
          checkoutData,
          item._id,
          analyticsParams,
          itemType,
        );
        const returnUrl = `${import.meta.env.VITE_STRIPE_ADAPTER_BASE_URL}/payment-return/${order._id}`;
        setClientSecret(order.clientSecret);
        setOrderInfo({
          normasTeachingItemName: item.itemName,
          amountCurrency: order.productPrice,
          returnUrl: `${returnUrl}`,
        });
      } catch (err) {
        console.error("Error creating order:", err);
        orderCreated.current = false;
        handleError("Impossibile inizializzare il pagamento.");
      }
    };

    initStripe();
  }, [
    checkoutData,
    item._id,
    item.itemName,
    analyticsParams,
    handleError,
    itemType,
    createStripeOrder,
  ]);

  if (loading || !clientSecret || !orderInfo) {
    return <FullPageLoading />;
  }

  return (
    <StripeProvider clientSecret={clientSecret}>
      <CardInstallmentsPaymentForm
        checkoutData={checkoutData}
        orderInfo={orderInfo}
        onSuccess={onSuccess}
        onError={onError}
        onBack={onBack}
      />
    </StripeProvider>
  );
}
